import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Globe } from "lucide-react";
import { nav, secondaryNav, site } from "../data/site";
import { Logo } from "./Logo";
import { useLang } from "../i18n";

const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`inline-flex items-center rounded-full border border-teal/20 bg-white p-0.5 ${className}`} data-testid="lang-toggle">
      {["en", "es"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}`}
          className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-colors ${
            lang === l ? "bg-teal text-white" : "text-ink/60 hover:text-teal"
          }`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-black/5 shadow-[0_4px_30px_rgba(0,0,0,0.04)]"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 h-[88px] flex items-center justify-between">
        <Logo />

        <ul className="hidden xl:flex items-center gap-0.5">
          {nav.map((item) => {
            const active = pathname === item.to;
            return (
              <li key={item.to}>
                <Link
                  to={item.to}
                  data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`relative whitespace-nowrap px-3 py-2 text-sm rounded-full transition-colors duration-200 ${
                    active
                      ? "text-teal font-medium"
                      : "text-ink/70 hover:text-ink"
                  }`}
                >
                  {t(item.label)}
                  {active && (
                    <motion.span
                      layoutId="nav-active"
                      className="absolute inset-0 -z-10 rounded-full bg-teal/10"
                      transition={{ type: "spring", stiffness: 400, damping: 32 }}
                    />
                  )}
                </Link>
              </li>
            );
          })}
          <li>
            <Link
              to="/careers"
              data-testid="nav-link-join-our-team"
              className={`relative whitespace-nowrap px-3 py-2 text-sm rounded-full transition-colors duration-200 ${
                pathname === "/careers" ? "text-teal font-medium" : "text-ink/70 hover:text-ink"
              }`}
            >
              {t("Join Our Team")}
            </Link>
          </li>
        </ul>

        <div className="hidden xl:flex items-center gap-2">
          <LangToggle />
          <Link
            to="/schedule-tour"
            data-testid="nav-schedule-tour-btn"
            className="inline-flex items-center whitespace-nowrap rounded-full border border-teal/30 px-3.5 py-2 text-sm font-medium text-teal hover:bg-teal/10 transition-colors duration-200"
          >
            {t("Schedule a Tour")}
          </Link>
          <Link
            to="/contact"
            data-testid="nav-request-info-btn"
            className="inline-flex items-center whitespace-nowrap rounded-full bg-teal px-4 py-2 text-sm font-medium text-white hover:bg-[#2E6088] transition-colors duration-200 shadow-sm hover:shadow"
          >
            {t("Request Info")}
          </Link>
        </div>

        <div className="flex items-center gap-2 xl:hidden">
          <LangToggle />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="grid place-items-center h-11 w-11 rounded-xl bg-teal/10 text-teal"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-black/5"
          >
            <ul className="px-5 py-4 flex flex-col gap-1">
              {nav.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`block px-4 py-3 rounded-xl text-base ${
                      pathname === item.to
                        ? "bg-teal/10 text-teal font-medium"
                        : "text-ink/80"
                    }`}
                  >
                    {t(item.label)}
                  </Link>
                </li>
              ))}
              {secondaryNav.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`block px-4 py-3 rounded-xl text-base ${
                      pathname === item.to
                        ? "bg-teal/10 text-teal font-medium"
                        : "text-ink/80"
                    }`}
                  >
                    {t(item.label)}
                  </Link>
                </li>
              ))}
              <li className="mt-2 flex flex-col gap-2">
                <a
                  href={site.phoneHref}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-teal/30 px-5 py-3 text-sm font-medium text-teal"
                >
                  <Phone size={16} /> {t("Call Us")} {site.phone}
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-full bg-teal px-5 py-3 text-sm font-medium text-white"
                >
                  {t("Request Information")}
                </Link>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
